import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'

/**
 * Roster member with Slack user ID
 * Used for support team rotation and Slack notifications
 */
export interface RosterMember {
  name: string
  slackId: string
}

/**
 * Support Request Type Enum
 * Predefined types of support requests that can be made
 */
export enum SupportRequestType {
  CUSTOM_BADGE = 'custom-badge',
  CUSTOM_EMAIL = 'custom-email-templates',
  CONDITIONAL_FIELDS = 'conditional-fields-setup',
  CUSTOM_REPORTS = 'custom-reports-exports',
  PAYMENT_GATEWAY = 'payment-gateway-integration',
  THIRD_PARTY_APPS = 'third-party-app-integrations',
  CUSTOM_LANDING_PAGE = 'custom-landing-page-modifications',
  SMS_NOTIFICATIONS = 'sms-notifications-setup',
  MULTI_LANGUAGE = 'multi-language-support',
  OTHER = 'other'
}

export const BackendRequestTypes: SupportRequestType[] = [
  SupportRequestType.CUSTOM_EMAIL,
  SupportRequestType.CONDITIONAL_FIELDS,
  SupportRequestType.CUSTOM_REPORTS,
  SupportRequestType.PAYMENT_GATEWAY,
  SupportRequestType.THIRD_PARTY_APPS,
  SupportRequestType.SMS_NOTIFICATIONS,
  SupportRequestType.OTHER
]

export const FrontendRequestTypes: SupportRequestType[] = [
  SupportRequestType.CUSTOM_BADGE,
  SupportRequestType.CUSTOM_EMAIL,
  SupportRequestType.CUSTOM_LANDING_PAGE,
  SupportRequestType.MULTI_LANGUAGE,
  SupportRequestType.OTHER
]

/**
 * Field selection for a persona
 * Maps persona slug to selected fields with labels and names
 */
export interface PersonaFieldSelection {
  personaSlug: string
  personaName: string
  fieldNames: string[] // Array of field names (for backward compatibility)
  fields?: PersonaField[] // Array of persona fields with all metadata
}

/**
 * User information for tracking who created the request
 */
export interface SupportRequestCreator {
  name: string
  email: string
}

/**
 * Support Request Interface
 * Represents a support request for an event
 */
export interface SupportRequest {
  /** Unique identifier for the support request */
  id: string
  /** Type of support request */
  type: SupportRequestType
  /** Detailed description in HTML format (from Quill editor) */
  description: string
  /** Optional array of persona slugs this request applies to */
  personas?: string[]
  /** Optional array of persona names for display */
  personaNames?: string[]
  /** Optional array of field selections per persona */
  fields?: PersonaFieldSelection[]
  /** Whether the request has been fulfilled */
  fulfilled: boolean
  /** ISO timestamp when the request was created */
  createdAt: string
  /** User who created the request */
  createdBy: SupportRequestCreator
  /** Whether the Slack message was successfully sent */
  slackMessageSent: boolean
  /** Name of the person assigned from roster rotation */
  assignedTo?: string
}

/**
 * Form data for creating a new support request
 */
export interface SupportRequestFormData {
  type: SupportRequestType
  description: string
  personas: string[]
  personaNames: string[]
  fields: PersonaFieldSelection[]
}

/**
 * Slack message payload for support requests
 */
export interface SupportRequestSlackPayload {
  eventName: string
  eventSlug: string
  workspaceName: string
  workspaceSlug: string
  requestType: string
  personas?: string[]
  personaNames?: string[]
  fields?: PersonaFieldSelection[]
  description: string
  assignedTo: string[]
  createdBy: SupportRequestCreator
}
